import React from 'react';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { LOC_CHOICE_ID, LOC_MAPPING, TOF_AUDIT_TRAIL } from 'constants/ocplcConstants';
import { feedSubtitleStyles } from 'pages/ocplcDashboard/ocplcHelpers';
import { Tooltip } from '@mui/material';
import { formatDateFieldForAudit } from 'pages/Utils/OcplcUtil';
import { ButtonSpinner } from 'components/ButtonSpinner';

const createColumn = (field, headerName, width) => ({
    field,
    headerName,
    width,
    sortable: false,
    renderCell: (params) => {
        let value = formatDateFieldForAudit(field, params.row);
        if (field === 'createdBy') {
            value = params?.row?.whoChanged || value
        }
        return (
            <Tooltip title={value}>
                <Box>{value}</Box>
            </Tooltip>
        );
    },
});

const tofColumns = [
    createColumn('version', 'Version', 80),
    createColumn('action', 'Action Type', 120),
    createColumn('region', 'Region', 80),
    createColumn('country', 'Country', 80),
    createColumn('productGroup', 'Product Group', 150),
    createColumn('productFamily', 'Product Family', 150),
    createColumn('businessModel', 'Business Model', 150),
    createColumn('status', 'Status', 80),
    createColumn('programType', 'Program Type', 150),
    createColumn('programs', 'Programs', 150),
    createColumn('startDate', 'Start Date', 150),
    createColumn('endDate', 'End Date', 150),
    createColumn('comments', 'Comments', 300),
    createColumn('whoChanged', 'Who Changed', 200),
    createColumn('createDate', 'Create Date', 150),
    createColumn('lastModifiedDate', 'Last Modified Date', 150),
    createColumn('actionSource', 'Source', 120),
    createColumn('actionDt', 'Action Date', 150),
    createColumn('actionBy', 'Action By', 120),
];

const modelColumns = [
    ...tofColumns.slice(0, 8),
    createColumn('ap', 'AP', 80),
    createColumn('eu', 'EU', 80),
    createColumn('la', 'LA', 80),
    createColumn('na', 'NA', 80),
    ...tofColumns.slice(10),
];

const parametersColumns=[
    createColumn('version', 'Version', 80),
    createColumn('action', 'Action Type', 120),
    createColumn('region', 'Region', 80),
    createColumn('productGroup', 'Product Group', 150),
    createColumn('productFamily', 'Product Family', 150),
    createColumn('feature', 'Parameter', 150),
    createColumn('text', 'Parameter Name', 200),
    createColumn('featureValue', 'Parameter Value', 150),
    createColumn('status', 'Status', 80),
    createColumn('comments', 'Comments', 300),
    createColumn('whoChanged', 'Who Changed', 180),
    createColumn('lastModifiedDate', 'When Changed', 180),
    createColumn('createdBy', 'Created By', 180),
    createColumn('actionSource', 'Source', 120),
    createColumn('actionDt', 'Action Date', 150),
    createColumn('actionBy', 'Action By', 120),
]

const defaultLocColumns=[
    createColumn('version', 'Version', 80),
    createColumn('action', 'Action Type', 120),
    createColumn('region', 'Region', 80),
    createColumn('country', 'Country', 80),
    createColumn('productGroup', 'Product Group', 120),
    createColumn('productFamily', 'Product Family', 120),
    createColumn('feature', 'Feature', 140),
    createColumn('featureValue', 'Feature Value', 140),
    createColumn('locFeature', 'Loc Feature', 180),
    createColumn('locFeatureValue', 'Loc Feature Value', 140),
    createColumn('status', 'Status', 80),
    createColumn('comments', 'Comments', 250),
    createColumn('whoChanged', 'Who Changed', 200),
    createColumn('lastModifiedDate', 'Last Modified Date', 200),
    createColumn('actionSource', 'Source', 120),
    createColumn('actionDt', 'Action Date', 150),
    createColumn('actionBy', 'Action By', 120),
]

const avilableLocLocColumns=[
    ...defaultLocColumns.slice(0,8),
    createColumn('startDate', 'WW START / REG START', 200),
    createColumn('endDate', 'WW END / REG END', 200),
    ...defaultLocColumns.slice(10,17),
]
const FeedDialog = (props) => {
    const dispatch = useDispatch();
    const { open, setOpenFeedDialog, feedRowData, selectedTable } = props;
    const {modelAuditTOFTrial, isLoading} = useSelector(state => state.ocplc);

    const getColumns = () =>
        ["modelOfferAudit", "modelOfferAuditV3", "materialOfferAuditV2","materialAvailabilityAuditV3","serviceOfferAudit","serviceAvailabilityAuditV3"].includes(feedRowData)
            ? modelColumns
            : feedRowData==="tofProgramManagement"?parametersColumns:feedRowData==="defaultLocAudit"?defaultLocColumns:feedRowData==="avilableLocAudit"?avilableLocLocColumns:tofColumns;

    const handleCloseFeedDialog = () => {
        setOpenFeedDialog(false);
        dispatch({ type: TOF_AUDIT_TRAIL, payload: {modelAuditTrial:[],isLoading:false} });
    }

    const getAuditTitle = () => {
        switch (feedRowData) {
            case "modelOfferAudit":
            case "modelOfferAuditV3":
                return "Audit Trail - Model";

            case "materialOfferAuditV2":
                case "materialAvailabilityAuditV3":
                    return "Audit Trail - Material";

            case "serviceOfferAudit":
                case "serviceAvailabilityAuditV3":
                return "Audit Trail - Factory Service";

            case "tofProgramManagement":
                if(selectedTable === LOC_CHOICE_ID){
                    return "Audit Trail - Localization Choice ID Customization";
                }else if (selectedTable === LOC_MAPPING){
                    return "Audit Trail - Localization Mapping";
                }else{
                    return "Audit Trail - TOF Program Management";
                }
            case "defaultLocAudit":
                return "Audit Trail – Default Localization";

            case "avilableLocAudit":
                return "Audit Trail – Available Localization";

            default:
                return "Marketing Program Audit Trail";
        }
    };

    return (
        <Paper>
            <Dialog open={open} onClose={handleCloseFeedDialog} maxWidth={false}
                sx={{
                    '& .MuiDialog-paper': {
                        width: '100%',
                        maxWidth: 'none',
                        borderRadius: '12px'
                    }
                }}>
                <DialogTitle>
                    {getAuditTitle()}{' '}
                    <span style={feedSubtitleStyles}>(Time stamp are based on UTC Zone)</span>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ height: 600, width: '100%' }}>
                        {isLoading ? <ButtonSpinner/> 
                             :
                            <DataGrid
                                rows={modelAuditTOFTrial?.map((row, index) => ({ id: index, ...row }))}
                                columns={getColumns()}
                                rowHeight={40}
                                disableSelectionOnClick
                                columnHeaderHeight={50}
                                hideFooterPagination={true}
                                hideFooter={true}
                                sx={{ '& .MuiDataGrid-columnHeaderTitleContainer': { display: 'flex' }, "& .MuiDataGrid-columnSeparator": { display: "none" }, }}
                                disableColumnFilter
                                disableColumnMenu
                                getRowClassName={(params) => `${params.indexRelativeToCurrentPage % 2 === 0 ? 'evenRow' : 'oddRow'}`}
                            />
                        }
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={handleCloseFeedDialog} className='cancel-button'>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default FeedDialog;